// move-decision.ts — Janus lane I2/I3 S3b (2026-09-28, owner: "this should all be frontend"): the BROWSER decides an inventory move
// (pickup / drop / give / take / bag moves) before it posts to move_draggable_inventory_inc.php. The rules are
// janus-engine/inventory/move-rules.ts (find_stack_target, the bag rules, the AP legs) + the existing twins (floor-move.ts weightGate,
// throw-object-gate.ts creatureHasFreeHand); this module binds them to the page's caches and answers the `client_decision` the endpoint
// persists. Missing context refuses before posting; timed doffing uses the browser equip envelope.
import { basicsVerdict, capacityVerdict, cycleVerdict, isContainer, keyringVerdict, moveApCost, needsDoff, stackTarget, takeAllApPrefix, type MoveRuleReaders, type Row } from '../janus-engine/inventory/move-rules';
import { weightGate, weightGateRequired } from '../janus-engine/inventory/floor-move';
import { casterCarriesRow, creatureHasFreeHand } from '../janus-engine/actions/throw-object-gate';
import { C1_CONTRACT } from '../janus-engine/compile/possessor-compile';
import { actorInCombat, createEquipPageReaders } from '../websocket/equip-page-readers';
import { timedEquipCampaignId, timedEquipTimerSkipped } from '../janus-engine/inventory/timed-equip-context.js';
import { rowScalar } from '../encounter-map/ft3d/perception';
import { computedValue } from '../shared/array-types';

export interface ClientMoveDecision { target: string | null; ap: { cost: number; in_combat: boolean; before: number | null; after: number | null } }
/** MV5 (N6c ask a): is `got` the SAME decision as `want` — the merge target and the whole AP block? A caller that already checked a
 *  decision binds the move to it; any difference refuses instead of re-deciding (into a merge, or a different AP charge). */
export function sameMoveDecision(got: ClientMoveDecision | null, want: ClientMoveDecision): boolean {
  return !!got && U(got.target) === U(want.target) && got.ap.cost === want.ap.cost && got.ap.in_combat === want.ap.in_combat
    && got.ap.before === want.ap.before && got.ap.after === want.ap.after;
}
/**
 * MV2 (Codex round 11 P2): does a carried RETRY match its remembered first attempt? The retry re-sends that attempt verbatim, so a call
 * that now requires a DIFFERENT decision (or a decision where the first attempt posted none), or asks for another move kind (`leg`), would
 * get a move it did not ask for — the caller refuses it instead. A retry carrying no requirement keeps the replay (the lost-answer contract).
 */
export function carriedRetryMatches(remembered: { decision: ClientMoveDecision | null; leg: string }, required: ClientMoveDecision | null, leg: string): boolean {
  if (remembered.leg !== leg) return false;
  return !required || sameMoveDecision(remembered.decision, required);
}
/** `doff` commits through the browser equip envelope. The historical `handback` discriminator names a pre-post refusal only. */
export type MoveDecisionResult = { decision: ClientMoveDecision } | { refuse: string } | { handback: string } | { doff: true };

const U = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const list = (v: unknown): unknown[] => Array.isArray(v) ? v : asRow(v) ? Object.values(asRow(v)!) : [];
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

interface Caches { casterCache?: Map<string, unknown>; encounterCache?: Map<string, unknown>; objectCache?: Map<string, unknown> }
const caches = (): Caches => ((window as unknown as { FateTide?: Caches }).FateTide) ?? {};

/** Every compiled copy of a creature row this page holds (caster cache first, then encounter members). */
function creatureRow(id: string): Row | null {
  const ID = U(id);
  const c = caches();
  if (c.casterCache instanceof Map) for (const [k, v] of c.casterCache) if (U(k) === ID && asRow(v)) return v as Row;
  if (c.objectCache instanceof Map) for (const value of c.objectCache.values()) {
    const row=asRow(value);
    if(row && U(rowScalar(row,'encounter_creature_id'))===ID)return row;
  }
  if (c.encounterCache instanceof Map) for (const enc of c.encounterCache.values()) {
    for (const m of list(asRow(enc)?.encounter_creatures_arr)) if (U(asRow(asRow(m)?.encounter_creatures)?.encounter_creature_id) === ID) return m as Row;
  }
  return null;
}
/** An inventory row anywhere in the page's caches, with the creature / encounter it sits under. */
function findRow(id: string): { row: Row; parentList: unknown } | null {
  const ID = U(id);
  let hit: { row: Row; parentList: unknown } | null = null;
  const walk = (nodes: unknown, depth: number): void => {
    if (hit || depth > 24) return;
    for (const n of list(nodes)) {
      const r = asRow(n); if (!r) continue;
      if (U(rowScalar(r,'inventory_id')) === ID) { hit = { row: r, parentList: nodes }; return; }
      walk(r.inventory_in_container, depth + 1);
    }
  };
  const c = caches();
  if (c.casterCache instanceof Map) for (const v of c.casterCache.values()) { walk(asRow(v)?.inventory, 0); if (hit) return hit; }
  if (c.encounterCache instanceof Map) for (const enc of c.encounterCache.values()) {
    walk(asRow(enc)?.encounter_inventories_arr, 0); if (hit) return hit;
    for (const m of list(asRow(enc)?.encounter_creatures_arr)) { walk(asRow(m)?.inventory, 0); if (hit) return hit; }
  }
  if (c.objectCache instanceof Map) for (const value of c.objectCache.values()) {
    walk([value],0);if(hit)return hit;
    walk(asRow(value)?.inventory,0);if(hit)return hit;
  }
  return hit;
}
function readersFor(caster: Row, encounter: Row | null): { pageRead: ReturnType<typeof createEquipPageReaders>; r: MoveRuleReaders } {
  const pageRead = createEquipPageReaders(caster, encounter);
  const r: MoveRuleReaders = {
    value: (row, key) => row ? pageRead.value(pageRead.fat(row), key) : null,
    itemType: row => row ? pageRead.itemType(row) : '',
    inventoryField: (row, key) => row ? rowScalar(row,key) : null,
    statusItemIds: row => [...list(asRow(row.statuses)?.target), ...list(asRow(row.statuses)?.caster)]
      .map(s => U(asRow(s)?.status_item_id ?? asRow(asRow(s)?.items)?.item_id)).filter(Boolean),
  };
  return { pageRead, r };
}
/** A number the page holds (a missing / non-numeric value is unknown: Number(null) would read as a known 0). */
function knownNumber(v: unknown): number | null {
  const x = Array.isArray(v) ? v[0] : v;
  if (typeof x === 'number') return Number.isFinite(x) ? x : null;
  return typeof x === 'string' && x.trim() !== '' && Number.isFinite(Number(x)) ? Number(x) : null;
}
function encounterOf(id: string): Row | null {
  const c = caches();
  if (!(c.encounterCache instanceof Map)) return null;
  return asRow(c.encounterCache.get(U(id)) ?? c.encounterCache.get(U(id).toLowerCase()));
}

/** The page's campaign blob for `campaignId` (campaignCache), or null. */
function campaignOf(campaignId: string): Row | null {
  const cache = (window as unknown as { FateTide?: { campaignCache?: Map<string, unknown> } }).FateTide?.campaignCache;
  if (!(cache instanceof Map) || !UUID.test(U(campaignId))) return null;
  return asRow(cache.get(U(campaignId)) ?? cache.get(U(campaignId).toLowerCase()) ?? cache.get(campaignId));
}
/** Is the ENCOUNTER fighting (ft_corpse_loot_encounter_in_combat: its in_combat column, or the looter in combat)? null = unknown. */
function lootInCombat(caster: Row, encounter: Row | null): boolean | null {
  const enc = asRow(encounter?.encounters) ?? encounter;
  if (enc && Number(enc.in_combat) === 1) return true;
  return actorInCombat(caster, encounter);
}
/** Skipped timers use the ordinary move: no campaign, campsite/stronghold, or a dead body's gear outside combat.
 * Missing saved context asks the async caller to load it before deciding. The posted campaign id is irrelevant. */
export function doffTimerSkipped(_campaignId: string, caster: Row, encounter: Row | null, source: Row, r: MoveRuleReaders): boolean | null {
  const contextId = timedEquipCampaignId(caster, encounter);
  const campaign = contextId ? campaignOf(contextId) : null;
  if (contextId && !campaign) return null; // the async move seam loads it before deciding
  if (timedEquipTimerSkipped(caster, encounter, campaign)) return true;
  const holder = U(r.inventoryField(source, 'possess_id'));
  if (holder === U(asRow(caster.encounter_creatures)?.encounter_creature_id)) return false;
  const member = list(encounter?.encounter_creatures_arr).map(asRow).find(m => U(asRow(m?.encounter_creatures)?.encounter_creature_id) === holder);
  if (!member) return null; // a swarm pile (pile gear counts as worn) or a holder the page does not hold
  if (Number(asRow(member.encounter_creatures)?.encounter_creature_dead ?? 0) !== 1) return false;
  const fighting = lootInCombat(caster, encounter);
  return fighting === null ? null : !fighting;
}

/**
 * Decide one move. Equip and timed doff use their own browser envelope.
 */
export function decideInventoryMove(input: {
  casterId: string; inventoryId: string; qty: number; moveToPossessId: string; moveType: string; encounterId: string; isGm: boolean; editorSurface: boolean;
  /** Legacy caller context; timed starts derive their campaign from the saved actor/encounter instead. */
  campaignId?: string;
  /** the POSTED ground pair (x_pos, z_pos) and ft3d_elevation, exactly as the move request carries them (a floor merge is position-exact) */
  xPos?: unknown; zPos?: unknown; elevation?: unknown;
  /** MV5 (N6c ask c): 'fast-draw' = the move leg that DRAWS an item out of a bag into the actor's hands costs 0 AP (the legacy equip_or_swap
   *  charged a Fast Draw weapon 0). Only a move_inventory onto the actor itself; anything else refuses. Default 'standard' (moveApCost). */
  apLeg?: 'standard' | 'fast-draw';
  /** MV5 (I2/I3 ask, the move half of a split-equip): split `qty` off a stack the ACTOR holds directly into a NEW row under the same
   *  holder (the split-equip then equips that row). Free: the equip that follows pays the draw (worker pick, owner to review). */
  splitInPlace?: boolean;
}): MoveDecisionResult {
  if (input.moveType === 'equip_inventory') return { handback: 'equip' };
  const hasCaster = UUID.test(U(input.casterId));
  if (!hasCaster && !input.isGm && !input.editorSurface) return { handback: 'no-caster' };
  // A GM arranging stored inventory without an actor still needs a native stacking/container verdict, but has no AP leg.
  const caster = hasCaster ? creatureRow(input.casterId) : {};
  if (!caster) return { handback: 'no-caster-row' };
  const encounter = encounterOf(input.encounterId || String(asRow(caster.encounter_creatures)?.encounter_id ?? ''));
  const { pageRead, r } = readersFor(caster, encounter);
  const found = findRow(input.inventoryId);
  if (!found) return { handback: 'no-row' };
  const source = found.row;
  const qty = Math.trunc(Number(input.qty));
  const toActor = input.moveType === 'move_inventory' && U(input.moveToPossessId) === U(input.casterId);
  if (input.apLeg === 'fast-draw' && !toActor) return { refuse: 'Fast Draw only draws an item into your own hands.' };
  if (input.splitInPlace) {
    // The move endpoint stores a same-holder partial move as a NEW row (handle_partial_move, target null) — validate_move_basics, which
    // refuses a move onto the same holder, is the browser's rule and is skipped for a posted decision. Here only: a stack the actor holds
    // DIRECTLY (not in a bag — that is an ordinary take-out), not equipped, and fewer than all of it.
    const holder = U(r.inventoryField(source, 'possess_id'));
    const rowQty = Math.trunc(Number(Array.isArray(r.inventoryField(source, 'item_qty')) ? (r.inventoryField(source, 'item_qty') as unknown[])[0] : r.inventoryField(source, 'item_qty') ?? 1));
    if (!toActor || holder !== U(input.casterId)) return { refuse: 'Only a stack you hold directly can be split this way.' };
    if (String(r.inventoryField(source, 'equipped') ?? '').trim() !== '') return { refuse: 'Unequip that stack before splitting it.' };
    if (!(qty >= 1) || !Number.isFinite(rowQty) || !(qty < rowQty)) return { refuse: 'Split off fewer than the whole stack.' };
    const combat = actorInCombat(caster, encounter);
    if (combat === null) return { handback: 'combat-unknown' };
    const known = knownNumber(rowScalar(caster, 'aps') ?? 0);
    if (known === null) return { handback: 'ap-unknown' };
    const before = known === null ? null : Math.trunc(known);
    return { decision: { target: null, ap: { cost: 0, in_combat: combat, before, after: before } } };
  }
  if (needsDoff(input.moveType, source, r)) {
    // The move endpoint skips the doffing timer (move_inventory's effective ignore_donning_doffing) with no campaign, at the campaign's
    // campsite / stronghold, and for a dead body's gear OUT of combat (ft_corpse_loot_ignores_doffing) — then it is an ordinary, instant
    // move, decided below. Otherwise the move STARTS doffing.
    const verdict = doffTimerSkipped(input.campaignId ?? '', caster, encounter, source, r);
    if (verdict === null) return { handback: 'doff-context' };
    if (!verdict) return { doff: true };
  }
  // normalize_target_for_stacking(): a non-container inventory destination is its holder (+ its position).
  let destId = U(input.moveToPossessId);
  const destItem = findRow(destId);
  const encounterId = U(input.encounterId);
  // the posted position as the endpoint reads it: get_data() stringifies each number at PHP's `precision` (14 significant digits), then
  // (float); a missing / empty value is 0. The stack search runs at (x_pos, y_pos = posted z_pos, z_pos = elevation or the row's own).
  const phpFloat = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return 0;
    const n = Number(v); if (!Number.isFinite(n)) return null;
    return Number(n.toPrecision(14));
  };
  // pos_coord(): the row's `position` block (legacy `pos_arr`), a [0]-wrapped value unwrapped. build_pos_arr() OMITS a zero axis (and the
  // whole block when all are zero); the columns are decimal(10,3) NOT NULL DEFAULT 0, so an absent axis IS 0. A present, non-numeric one
  // is unknown (null).
  const coordOrNull = (row: Row | null, k: string): number | null => {
    const block = asRow(row?.position) ?? asRow(row?.pos_arr);
    let v: unknown = block && k in block ? block[k] : r.inventoryField(row, k);
    if (v === undefined || v === null) return 0;
    if (Array.isArray(v)) v = v[0];
    return knownNumber(v);
  };
  const coord = (row: Row | null, k: string): number => coordOrNull(row, k) ?? 0; // the endpoint's `pos_coord(...) ?? 0`
  let pos: { x: number | null; y: number | null; z: number | null } = { x: phpFloat(input.xPos), y: phpFloat(input.zPos),
    z: input.elevation !== null && input.elevation !== undefined && input.elevation !== '' && Number.isFinite(Number(input.elevation))
      ? phpFloat(input.elevation) : coord(source, 'z_pos') };
  if (destItem && !isContainer(destItem.row, r)) {
    destId = U(r.inventoryField(destItem.row, 'possess_id'));
    // normalize_target_for_stacking(): dropped ONTO a loose row → its holder, at its position
    pos = { x: coord(destItem.row, 'x_pos'), y: coord(destItem.row, 'y_pos'), z: coord(destItem.row, 'z_pos') };
  }
  const destIsFloor = destId === encounterId && UUID.test(destId);
  const destContainer = destItem && isContainer(destItem.row, r) ? destItem.row : null;
  const destCreature = !destIsFloor && !destContainer ? creatureRow(destId) : null;
  if (!destIsFloor && !destContainer && !destCreature) return { handback: 'no-destination' };
  // ---- rules (validate_move_basics / cycle / keyring / capacity) ----
  const sameHolder = U(r.inventoryField(source, 'possess_id')) === destId;
  const basics = basicsVerdict(qty, U(r.inventoryField(source, 'possess_id')), destId, !r.inventoryField(source, 'equipped'), !destIsFloor);
  if ('ok' in basics && !basics.ok) return sameHolder && !destIsFloor ? { refuse: basics.error } : { handback: 'basics' };
  const cycle = cycleVerdict(source, destId, r);
  if ('ok' in cycle && !cycle.ok) return { refuse: cycle.error };
  if (destContainer) {
    const keyring = keyringVerdict(destContainer, source, r);
    if ('ok' in keyring && !keyring.ok) return { refuse: keyring.error };
    const held = knownNumber(asRow(asRow(destContainer.computed)?.weight)?.held);
    const gross = knownNumber(asRow(asRow(source.computed)?.weight)?.gross ?? asRow(asRow(source.computed)?.weight)?.total);
    const cap = capacityVerdict(destContainer, source, qty, held, gross, r);
    if ('unknown' in cap) return { handback: 'capacity:' + cap.unknown };
    if (!cap.ok) return { refuse: cap.error };
  }
  // ---- the endpoint's guards (the player path; a GM / editor bypasses them, as PHP does) ----
  const sourceHolder = U(r.inventoryField(source, 'possess_id'));
  const fromFloor = sourceHolder === encounterId && UUID.test(encounterId);
  if (!input.isGm && !input.editorSurface) {
    if (r.value(source, 'immovable')) return { refuse: 'That object is fixed in place and cannot be moved.' };
    if (r.itemType(source) === 'digsite') return { refuse: 'The digsite is part of the terrain — it cannot be moved, only excavated.' };
    const holderRow = findRow(sourceHolder)?.row ?? null;
    if ((holderRow && r.itemType(holderRow) === 'digsite') || (destContainer && r.itemType(destContainer) === 'digsite')) {
      return { refuse: 'Those goods are still buried — excavate the digsite to unearth them.' };
    }
    if (fromFloor && (input.moveType === 'move_inventory')) {
      const free = creatureHasFreeHand(caster);
      if (free === null) return { handback: 'free-hand' };
      if (!free) return { refuse: 'Your hands are full — free a hand to pick that up.' };
    }
    if (destIsFloor && (input.moveType === 'drop_inventory' || input.moveType === 'move_inventory')) {
      // ft_throw_object_is_held_by() + ft_throw_object_mode() === 'draw': carried (at any depth) but not in a hand slot.
      const S = C1_CONTRACT.equipmentSlots;
      const inHand = [S.main_hand, S.off_hand, S.both_hands].map(U).includes(U(r.inventoryField(source, 'equipped')));
      const gateRead = { value: (row: Row | null, k: string) => r.value(row, k), inventoryField: (row: Row | null, k: string) => r.inventoryField(row, k),
        itemField: (row: Row | null, k: string) => (k === 'item_type' ? r.itemType(row) : asRow(row?.items)?.[k]), creatureSizeId: () => null, itemSizeId: () => null };
      const carried = casterCarriesRow(caster, U(input.casterId), source, gateRead);
      if (carried && !inHand) {
        const free = creatureHasFreeHand(caster);
        if (free === null) return { handback: 'free-hand' };
        if (!free) return { refuse: 'Your hands are full — free a hand to take that out.' };
      }
    }
  }
  if (weightGateRequired(fromFloor ? 'encounter' : 'other', input.isGm || input.editorSurface, input.editorSurface, hasCaster)) {
    const cap = asRow(asRow(asRow(caster.computed)?.creature)?.capacity);
    const capacityRaw = asRow(cap?.push_drag_lift)?.total ?? pageRead.value(caster, 'creature_push_drag_lift_capacity');
    const w = weightGate({ grossStackLb: Number(asRow(asRow(source.computed)?.weight)?.gross ?? NaN) || null, layerWeightLb: Number(r.value(source, 'weight') ?? NaN) || null,
      itemQty: Number(r.inventoryField(source, 'item_qty') ?? 1), capacityRaw }, qty);
    if (!w.allowed) {
      if (w.reason === 'capacity_unavailable') return { handback: 'weight-capacity' };
      return { refuse: `That object weighs ${Math.round(w.movedLb * 100) / 100} lb; your push, drag, or lift limit is ${Math.round((w.capacityLb ?? 0) * 100) / 100} lb.` };
    }
  }
  // ---- the stack it merges into (find_stack_target) — a floor merge is position-exact server-side: hand it back if one could apply ----
  // inventory_root_creature_id() on both sides: climb inventory rows to a creature; a floor / anything else has no root (null); a row the
  // page does not hold is undecidable.
  const rootOf = (start: string): string | null | undefined => {
    let id = U(start);
    for (let g = 0; g < 40 && UUID.test(id); g++) {
      if (creatureRow(id)) return id;
      const row = findRow(id);
      if (row) { id = U(r.inventoryField(row.row, 'possess_id')); continue; }
      return id === encounterId ? null : undefined;
    }
    return null;
  };
  const sourceRoot = rootOf(sourceHolder), destRoot = rootOf(destId);
  if (sourceRoot === undefined || destRoot === undefined) return { handback: 'wares-root' };
  const wares = (destRoot !== null && sourceRoot === destRoot && Number(r.inventoryField(source, 'inventory_wares') ?? 0) ? 1 : 0) as 0 | 1;
  let target: string | null = null;
  if (destIsFloor) {
    // A floor stack merges only at the EXACT position (find_stack_target's x/y/z filter). A floor → floor reposition settles its z
    // (ft_inventory_rest_z) before the search — that lane is I1's; hand it back.
    if (fromFloor) return { handback: 'floor-reposition' };
    if (pos.x === null || pos.y === null || pos.z === null) return { handback: 'floor-position' };
    // the stack search compares the SQL columns; the page holds them in each row's position block — carried onto the bucket it reads
    const floorRows = list(encounter?.encounter_inventories_arr).map(asRow).filter((x): x is Row => !!x)
      .filter(c => U(r.inventoryField(c, 'possess_id')) === encounterId);
    // a same-item pile whose position the page cannot read could be the one SQL matches
    if (floorRows.some(c => U(r.inventoryField(c, 'inventory_item_id')) === U(r.inventoryField(source, 'inventory_item_id'))
      && ['x_pos', 'y_pos', 'z_pos'].some(k => coordOrNull(c, k) === null))) return { handback: 'floor-position-unknown' };
    const floorAt = floorRows.map(c => ({ ...c, inventory: { ...asRow(c.inventory), x_pos: coord(c, 'x_pos'), y_pos: coord(c, 'y_pos'), z_pos: coord(c, 'z_pos') } }));
    const pick = stackTarget(source, floorAt, { isFloor: true, x: pos.x, y: pos.y, z: pos.z }, 0, r);
    if ('unknown' in pick) return { handback: pick.unknown };
    // a raw scene double whose 14-digit form differs could fall either side of a stored coordinate — undecidable only then
    const rawX = Number(input.xPos ?? 0), rawY = Number(input.zPos ?? 0);
    if (!destItem && (rawX !== pos.x || rawY !== pos.y)) {
      const raw = stackTarget(source, floorAt, { isFloor: true, x: rawX, y: rawY, z: pos.z }, 0, r);
      if ('unknown' in raw || raw.target !== pick.target) return { handback: 'floor-position-precision' };
    }
    target = pick.target;
  } else {
    const children = (destContainer ? destContainer.inventory_in_container : destCreature!.inventory) ?? [];
    if (!Array.isArray(children) && !asRow(children)) return { handback: 'dest-children' };
    const candidates = list(children).map(asRow).filter((x): x is Row => !!x).filter(c => U(r.inventoryField(c, 'possess_id')) === destId);
    const pick = stackTarget(source, candidates, { isFloor: false, x: 0, y: 0, z: 0 }, wares, r);
    if ('unknown' in pick) return { handback: pick.unknown };
    target = pick.target;
  }
  // AP is decided here; the endpoint only CASes the stored before and saves after.
  const combat = actorInCombat(caster, encounter);
  if (combat === null) return { handback: 'combat-unknown' };
  // MV5: the Fast Draw leg (toActor checked above) is free; the caller (the bag-equip chain) decides whether Fast Draw applies.
  const cost = combat ? (input.apLeg === 'fast-draw' ? 0 : moveApCost(input.moveType, source, r)) : 0;
  const beforeKnown = knownNumber(rowScalar(caster, 'aps') ?? 0);
  if (beforeKnown === null) return { handback: 'ap-unknown' };
  const before = beforeKnown === null ? null : Math.trunc(beforeKnown);
  if (combat && cost > 0 && before !== null && cost > before) return { refuse: `In Combat: This action requires ${cost} AP!` };
  return { decision: { target, ap: { cost, in_combat: combat, before, after: before === null ? null : before - cost } } };
}

export interface ClientTakeAllDecision { rows: Record<string, { target: string | null; ap: number; before: number | null; after: number | null }>;
  ap: { in_combat: boolean; before: number | null; after: number | null } }
/** `entries`: the leading entries the page decided to take (all of them unless combat AP ran out — D-021); `short` says so. */
export type TakeAllDecisionResult = { decision: ClientTakeAllDecision; entries: { inventory_id: string; item_qty: number }[];
  short: { taken: number; total: number; message: string } | null } | { refuse: string } | { handback: string };

/**
 * Decide a Take All (lane I2/I3 S4c): the batch RULES ft_take_all_preflight() ran, plus each row's merge target and AP leg -- the keyring
 * (a bag destination), every ancestor bag's pounds capacity with the batch's added weight (a fixed-weight bag absorbs it), the caster's
 * carry maximum, each row's stack in the destination simulated IN ORDER, and the AP. The server keeps the rest of the preflight.
 * Worn gear (a corpse's equipped rows), a row or bag the page does not hold, and a later row that could merge into an earlier one hand back.
 */
export function decideTakeAll(input: { casterId: string; sourceId: string; destinationId: string; encounterId: string;
  entries: { inventory_id: string; item_qty: number }[] }): TakeAllDecisionResult {
  const actor = U(input.casterId);
  if (!UUID.test(actor)) return { handback: 'no-caster' };
  const caster = creatureRow(actor);
  if (!caster) return { handback: 'no-caster-row' };
  const encounter = encounterOf(input.encounterId || String(asRow(caster.encounter_creatures)?.encounter_id ?? ''));
  const { pageRead, r } = readersFor(caster, encounter);
  const destId = U(input.destinationId);
  const destBag = destId === actor ? null : findRow(destId)?.row ?? null;
  if (destId !== actor && (!destBag || !isContainer(destBag, r))) return { handback: 'destination' };
  const rows: Row[] = [];
  let weight = 0;
  // A body's WORN gear (donning lane 2026-09-29): out of combat it comes off at once (the corpse-loot rule, ft_corpse_loot_ignores_doffing),
  // in combat the preflight refuses it — a looter doffs one piece at a time. The preflight still proves each worn row is the body's own.
  let fighting: boolean | null | undefined;
  for (const entry of input.entries) {
    const row = findRow(entry.inventory_id)?.row;
    if (!row) return { handback: 'no-row' };
    if (Number(r.inventoryField(row, 'item_qty') ?? 1) !== Number(entry.item_qty)) return { handback: 'qty' };
    if (String(r.inventoryField(row, 'equipped') ?? '').trim() !== '') {
      const holder = U(r.inventoryField(row, 'possess_id'));
      const body = list(encounter?.encounter_creatures_arr).map(asRow).find(m => U(asRow(m?.encounter_creatures)?.encounter_creature_id) === holder);
      if (!body || Number(asRow(body.encounter_creatures)?.encounter_creature_dead ?? 0) !== 1) return { handback: 'worn' }; // a pile / not a body
      if (fighting === undefined) fighting = lootInCombat(caster, encounter);
      if (fighting === null) return { handback: 'worn-combat-unknown' };
      if (fighting) return { refuse: 'In combat, worn gear must be doffed one piece at a time.' };
    }
    if (destBag) { const k = keyringVerdict(destBag, row, r); if ('ok' in k && !k.ok) return { refuse: k.error }; }
    weight += knownNumber(computedValue(row, 'weight', 'gross')) ?? knownNumber(computedValue(row, 'weight', 'total')) ?? 0;
    rows.push(row);
  }
  // Each ancestor receives the added weight until a fixed-weight container absorbs it.
  let cursor = destId;
  const seen = new Set<string>();
  while (cursor !== actor) {
    if (seen.has(cursor) || seen.size >= 32) return { handback: 'chain' };
    seen.add(cursor);
    const bag = cursor === destId ? destBag : findRow(cursor)?.row ?? null;
    if (!bag || !isContainer(bag, r)) return { handback: 'chain' };
    const cap = knownNumber(r.value(bag, 'carrying_capacity_pounds'));
    if (cap !== null) {
      const bw = asRow(asRow(bag.computed)?.weight);
      if (!bw) return { handback: 'bag-weight' };
      if ((knownNumber(bw.held) ?? 0) + weight > cap + 0.0001) return { refuse: 'Cannot take all: the destination container would exceed its carrying capacity.' };
    }
    const fixed = r.value(bag, 'carrying_capacity_fixed_weight');
    if (fixed !== null && fixed !== undefined && fixed !== '' && !(Array.isArray(fixed) && fixed.length === 0)) weight = 0;
    cursor = U(r.inventoryField(bag, 'possess_id'));
  }
  const creature = asRow(asRow(caster.computed)?.creature);
  const held = creature ? knownNumber(computedValue(creature, 'weight', 'total_weight_held') ?? 0) : null;
  const maximum = knownNumber(pageRead.value(caster, 'creature_max_carrying_capacity'));
  if (held === null || maximum === null) return { handback: 'carry' };
  if (held + weight > maximum + 0.0001) return { refuse: 'Cannot take all: this would exceed your maximum carrying capacity.' };
  // Stacks, in order. The source is a floor container / a dead body, never the actor's tree, so the wares cohort is 0 (PHP's root rule).
  const children = (destBag ? destBag.inventory_in_container : caster.inventory) ?? [];
  if (!Array.isArray(children) && !asRow(children)) return { handback: 'dest-children' };
  const candidates = list(children).map(asRow).filter((x): x is Row => !!x).filter(c => U(r.inventoryField(c, 'possess_id')) === destId);
  const moved = new Set<string>();
  const out: ClientTakeAllDecision['rows'] = {};
  const combat = actorInCombat(caster, encounter);
  if (combat === null) return { handback: 'combat-unknown' };
  // D-021 (owner ruling 2026-09-30, parity with the legacy partial haul): in combat the rows land IN ORDER until the actor's AP runs out.
  // The batch RULES above ran on the whole list the window posted, as legacy's preflight did; the AP then picks how many leading rows
  // are taken, and only those are posted — PHP persists exactly what the page decided.
  const beforeRaw = knownNumber(rowScalar(caster, 'aps') ?? 0);
  const before = beforeRaw === null ? null : Math.trunc(beforeRaw);
  if (before === null) return { handback: 'ap-unknown' };
  const costs = rows.map(row => combat ? moveApCost('move_inventory', row, r) : 0);
  const prefix = takeAllApPrefix(costs, before, combat);
  if (prefix.unknown) return { handback: 'ap-unknown' };
  if (prefix.refuse) return { refuse: prefix.refuse };
  const taken = rows.slice(0, prefix.count);
  let balance = before;
  for (const [i, row] of taken.entries()) {
    const id = U(r.inventoryField(row, 'inventory_id'));
    const pick = stackTarget(row, candidates, { isFloor: false, x: 0, y: 0, z: 0 }, 0, r);
    if ('unknown' in pick) return { handback: pick.unknown };
    if (pick.target !== null && moved.has(pick.target)) return { handback: 'batch-chain-merge' };
    if (pick.target === null) {
      // the whole row arrives under the destination: a candidate for a LATER row (handed back above rather than modelled field by field)
      moved.add(id);
      candidates.push({ ...row, inventory: { ...asRow(row.inventory), possess_id: destId, equipped: '', inventory_wares: 0 } });
    }
    const after = balance - costs[i]!;
    out[id] = { target: pick.target, ap: costs[i]!, before: balance, after };
    balance = after;
  }
  const entries = input.entries.slice(0, prefix.count);
  const short = prefix.count < rows.length
    ? { taken: prefix.count, total: rows.length, message: `In Combat: This action requires ${costs[prefix.count]} AP! Took ${prefix.count} of ${rows.length} before your AP ran out.` }
    : null;
  return { decision: { rows: out, ap: { in_combat: combat, before, after: balance } }, entries, short };
}
